import { adminNoticePageState } from "@/atoms/notice/admin-notice-page-state";
import { pageNumberState } from "@/atoms/order/page-number-state";
import { DEBOUNCE_TIME } from "@/common/constant/debounce-time";
import { PaginationSize } from "@/common/enum/pagination-size";
import { AdminNoticeHeader } from "@/components/header/AdminNotificationHeader";
import { CreateAdminNoticeModal } from "@/components/modal/CreateAdminNoticeModal";
import { DeleteAdminNoticeModal } from "@/components/modal/DeleteAdminNoticeModal";
import { UpdateAdminNoticeModal } from "@/components/modal/UpdateAdminNoticeModal";
import { AdminNoticeTable } from "@/components/table/admin-notice/AdminNoticeTable";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { noticeService } from "@/services/notice-service";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { NoticeDetailDto } from "@/types/dto/notice-detail-dto";
import { NoticePagePayload } from "@/types/payload/notice-page-payload";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export default function AdminNoticePage() {
  const [noticePage, setNoticePage] =
    useRecoilState<BasePageDto<NoticeDetailDto>>(adminNoticePageState);
  const [selectedNotice, setSelectedNotice] = useState<
    NoticeDetailDto | undefined
  >();
  const [isOpenCreateNoticeModal, setIsOpenCreateNoticeModal] = useState(false);
  const [isOpenDeleteNoticeModal, setIsOpenDeleteNoticeModal] = useState(false);

  const refreshPage = async () => {
    const payload: NoticePagePayload = {
      page: pageNumber,
      size: PaginationSize.SIZE_10,
    };
    const response = await noticeService.getPage(payload);
    if (!response.data || !response.ok) return;

    const newTotalPage = response.data.totalPage;

    if (pageNumber > newTotalPage && newTotalPage > 0) {
      const fallbackResponse = await noticeService.getPage({
        page: newTotalPage,
        size: PaginationSize.SIZE_10,
      });
      if (fallbackResponse.ok && fallbackResponse.data) {
        setNoticePage(fallbackResponse.data);
        setPageNumber(newTotalPage);
      }
      return;
    }
    setNoticePage(response.data);
  };

  const handleCreateNoticeModalOpen = () => {
    setIsOpenCreateNoticeModal(true);
  };

  const handleCreateNoticeModalClose = () => {
    setIsOpenCreateNoticeModal(false);
  };

  const handleDeleteNoticeModalOpen = () => {
    setIsOpenDeleteNoticeModal(true);
  };

  const handleDeleteNoticeModalClose = () => {
    setIsOpenDeleteNoticeModal(false);
  };

  const handlePageChange = (currentPage: number) => {
    setNoticePage({ ...noticePage, currentPage });
  };

  const clearSelectedNotice = () => {
    setSelectedNotice(undefined);
  };

  const handleNoticeNameClick = async (id: number) => {
    try {
      const response = await noticeService.getById({ id });
      if (!response.ok) {
        return;
      }
      setSelectedNotice(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getPage = async (input: NoticePagePayload) => {
    try {
      const response = await noticeService.getPage(input);
      if (!response.ok) {
        return;
      }
      setNoticePage(response.data);
      console.log(response);
    } catch (error) {
      console.log(error);
    }
  };

  const [pageNumber, setPageNumber] = useRecoilState(pageNumberState);

  const getPageDebounce = useCallback(
    debounce(async (input) => {
      await getPage(input);
    }, DEBOUNCE_TIME),
    []
  );

  useEffect(() => {
    getPageDebounce({
      page: pageNumber,
      size: PaginationSize.SIZE_10,
    });
  }, [pageNumber, getPageDebounce]);

  useEffect(() => {}, [selectedNotice, isOpenDeleteNoticeModal]);

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <AdminNoticeHeader openModal={handleCreateNoticeModalOpen} />
        <AdminNoticeTable
          page={noticePage}
          handleNoticeNameClick={handleNoticeNameClick}
          handlePageChange={handlePageChange}
          onSuccess={refreshPage}
        />
        {selectedNotice && (
          <UpdateAdminNoticeModal
            selectedNotice={selectedNotice}
            clearSelectedNotice={clearSelectedNotice}
            handleDeleteNoticeModalOpen={handleDeleteNoticeModalOpen}
          />
        )}
        {isOpenCreateNoticeModal && (
          <CreateAdminNoticeModal
            closeModal={handleCreateNoticeModalClose}
            onSuccess={refreshPage}
          />
        )}
        {selectedNotice && isOpenDeleteNoticeModal && (
          <DeleteAdminNoticeModal
            notice={selectedNotice}
            closeModal={handleDeleteNoticeModalClose}
            clearSelectedNotice={clearSelectedNotice}
            onSuccess={refreshPage}
          />
        )}
      </div>
    </PageWrapper>
  );
}
